import { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Card, Col, Container, Row } from "react-bootstrap";

import TenantList from "../Common/TenantList/TenantList";
import { useAppState } from "../../state";

import ToggleFeatureFlagsPage from "./ToggleFeatureFlagsPage";

import "../TenantSetup/TenantSetup.scss";
import "./FeatureFlagPage.scss";

/** Minimal tenant shape accepted by TenantList selection (id is required). */
interface SelectedTenant {
  id: number;
  [key: string]: unknown;
}

export default function FeatureFlagPage() {
  const { user } = useAppState();
  const history = useHistory();
  const location = useLocation();

  const initialTenant = useMemo((): SelectedTenant | undefined => {
    const tenantId = parseInt(
      new URLSearchParams(location.search).get("tenantId") ?? "",
      10
    );
    return tenantId > 0 ? { id: tenantId } : undefined;
  }, [location.search]);

  const [selectedTenant, setSelectedTenant] = useState<SelectedTenant | undefined>(
    initialTenant
  );
  const [howToExpanded, setHowToExpanded] = useState(true);

  const handleSelectTenant = (tenant: SelectedTenant | undefined) => {
    setSelectedTenant(tenant);
    // Drop ?tenantId= once the user picks from the list, matching other setup pages.
    history.push(location.pathname);
  };

  return (
    <Container fluid className="setupView" id="featureFlagPage">
      <Row>
        <TenantList
          user={user}
          onSelectTenant={(t: SelectedTenant) => handleSelectTenant(t)}
          selectedTenant={selectedTenant}
        />
        {selectedTenant && (
          <Col md="9" id="featureFlagMainPanel" className="featureFlagMainPanel">
            <h3 id="pageHeader">Conditional Feature Flags</h3>
            <Card className="bg-white border-0" id="flagHowTo">
              <Card.Body>
                <button
                  type="button"
                  className="flagHowToToggle"
                  aria-expanded={howToExpanded}
                  aria-controls="flagHowToBody"
                  onClick={() => setHowToExpanded((open) => !open)}
                >
                  <i
                    className={`fa ${howToExpanded ? "fa-chevron-down" : "fa-chevron-right"}`}
                    aria-hidden="true"
                  />
                  <span className="flagHowToTitle">How to utilize Feature Flags</span>
                </button>
                {howToExpanded && (
                  <div className="flagHowToText" id="flagHowToBody">
                    <p>
                      Conditional Feature Flags are master switches for feature
                      availability — globally, or more granularly at a tenant or
                      facility level.
                    </p>
                    <p>
                      If a flag is not enabled, dates and granular assignments are
                      ignored and the feature is unavailable to everyone. Use this
                      for scheduled rollouts or to pull functionality without an
                      elevate.
                    </p>
                    <p>
                      Global start/end dates control when an enabled flag is
                      available; tenant and facility assignments can narrow that
                      further. With no assignments, the flag is a global on/off
                      switch. Once any tenant or facility is associated, only those
                      associations get access.
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>
            <ToggleFeatureFlagsPage selectedTenantId={selectedTenant.id} />
          </Col>
        )}
      </Row>
    </Container>
  );
}
